'use client';
import { registerForum } from '@ssv-website/hooks';
import { useState } from 'react';
import {
  Button,
  Checkbox,
  DialogOptions,
  DialogPlugin,
  Form,
  FormProps,
  MessagePlugin,
} from 'tdesign-react';
import copy from 'copy-to-clipboard';

const CreateRegisterInfoBody = ({
  data,
  onSubmit,
}: {
  onSubmit: (fields: any) => void;
  data: string;
}) => {
  const info = formatIntlRegisterInfo(data);
  const originList = info.list;
  const [list] = useState(originList);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [form] = Form.useForm();
  const handleSubmit: FormProps['onSubmit'] = (values) => {
    onSubmit(values.fields);
  };

  const handleCheckboxChange = (checkedValues: any[]) => {
    // 1. 按radioKey分组，筛选出有radioKey的组
    const radioGroups = new Map<string, any[]>(); // key: radioKey, value: 该组选中项
    checkedValues.forEach((item) => {
      if (item.radioKey) {
        if (!radioGroups.has(item.radioKey)) {
          radioGroups.set(item.radioKey, []);
        }
        radioGroups.get(item.radioKey)?.push(item);
      }
    });

    // 2. 处理互斥：同一radioKey组只保留最后一个选中项
    let finalValues = [...checkedValues];
    radioGroups.forEach((groupItems, radioKey) => {
      if (groupItems.length > 1) {
        // 移除该组所有选中项，只保留最后一个
        finalValues = finalValues.filter(
          (item) =>
            !(
              item.radioKey === radioKey &&
              !Object.is(item, groupItems[groupItems.length - 1])
            )
        );
      }
    });

    // 3. 更新选中状态（同步到Form和本地状态）
    setSelectedItems(finalValues);
    form.setFieldsValue({ forum: finalValues }); // 确保Form值同步
  };
  // 核心逻辑：动态计算每个选项是否禁用
  const getIsDisabled = (item: any) => {
    // 无radioKey的选项永不禁用
    if (!item.radioKey) return false;
    // 有radioKey的选项：如果同组已有其他选中项，则禁用
    return selectedItems.some(
      (selected) =>
        selected.radioKey === item.radioKey && selected.value !== item.value
    );
  };
  return (
    <>
      <div className="flex flex-col mb-5">
        <div className="text-2xl  text-[#121c59] font-700">
          Please select the session(s) you wish to attend
        </div>
        <div className="text-sm text-[#121c59]/40">
          Both the Opening Main Forum and Closing Forum are open for
          registration; parallel sub-forums will be held simultaneously, and
          attendees may only select one session; the exhibition zone is open all
          day; the CarbonXmade New Product Launch will take place at midday.
        </div>
      </div>

      <Form labelAlign={'top'} form={form} onSubmit={handleSubmit}>
        <div className="max-h-[300px] overflow-y-auto">
          <Form.FormItem label="" name="forum" className="text-lg font-500">
            <Checkbox.Group onChange={handleCheckboxChange}>
              {list.map((item: any, index: number) => (
                <Checkbox
                  key={index}
                  value={item}
                  disabled={getIsDisabled(item)}
                >
                  {item.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.FormItem>
        </div>
        <div className="flex flex-col items-center justify-center mt-8">
          <Button
            block
            variant="base"
            type="submit"
            className="!h-10 !bg-[#1C3FF0]"
          >
            Submit
          </Button>
        </div>
      </Form>
    </>
  );
};

function formatIntlRegisterInfo(data: string | undefined) {
  let result = null;
  try {
    const list = JSON.parse(data || '[]');
    const info = list[0]?.data || '{}';
    result = JSON.parse(info);
  } catch (error) {
    result = null;
    console.error(error);
  }
  return result;
}
// 国际版-报名弹窗
export function ShowRegistrationModal({
  onSubmitSuccess,
  isMobile = false,
  data,
  options = {},
}: {
  options?: DialogOptions;
  data?: string;
  isMobile: boolean;
  onSubmitSuccess: () => void;
}) {
  const handleSubmit = (fields: any) => {
    const forum: any[] = fields.forum || [];
    const selectForum = forum.map((item: any) => item.label).join(';');
    if (!selectForum) {
      MessagePlugin.info({
        content: 'Please select the session(s) you wish to attend',
      });
      return;
    }
    registerForum({ forum: selectForum }).then(() => {
      modalInstance.hide();
      onSubmitSuccess?.();
      ShowRegisterSuccessModal({
        isMobile,
      });
    });
  };
  const modalInstance = DialogPlugin.alert({
    closeBtn: true,
    closeOnEscKeydown: true,
    closeOnOverlayClick: true,
    preventScrollThrough: true,
    footer: false,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    destroyOnClose: true,
    body: <CreateRegisterInfoBody data={data || ''} onSubmit={handleSubmit} />,
    onClose: () => {
      modalInstance.hide();
    },
    ...options,
  });
  return modalInstance;
}

// 国际版 - 预约弹窗
export function ShowBookingLiveModal({
  isMobile = false,
  liveUrl,
  options = {},
}: {
  liveUrl: string;
  options?: DialogOptions;
  isMobile: boolean;
}) {
  const modalInstance = DialogPlugin.confirm({
    header: <h5>Successfully Scheduled</h5>,
    body: (
      <p>
        The livestream link has been sent to your registered email address via
        email.
      </p>
    ),

    cancelBtn: 'Copy Link',
    confirmBtn: 'OK',
    closeOnEscKeydown: true,
    closeOnOverlayClick: true,
    preventScrollThrough: true,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    ...options,
    onClose: () => {
      modalInstance.hide();
    },
    onConfirm: () => {
      modalInstance.hide();
    },
    onCancel: () => {
      copy(liveUrl);
      MessagePlugin.success({
        content: 'Copy Success',
        duration: 1000,
      });
    },
  });
  return modalInstance;
}

// 国际版 - 报名成功弹窗
export function ShowRegisterSuccessModal({
  isMobile = false,
  options = {},
}: {
  options?: DialogOptions;
  isMobile: boolean;
}) {
  const modalInstance = DialogPlugin.confirm({
    closeOnEscKeydown: false,
    closeOnOverlayClick: false,
    preventScrollThrough: true,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    cancelBtn: false,
    closeBtn: false,
    confirmBtn: 'OK',
    header: <h5>Submission Complete</h5>,
    body: (
      <p>
        Your application is under review. Results will be sent to your
        registered email address. Due to venue capacity limitations, we cannot
        guarantee approval for all applicants.
      </p>
    ),
    onClose() {
      modalInstance.hide();
    },
    onConfirm() {
      modalInstance.hide();
    },
    ...options,
  });
  return modalInstance;
}

// 国际版 - 重复报名弹窗
export function ShowRepeatRegisterModal({
  isMobile = false,
  options = {},
}: {
  options?: DialogOptions;
  isMobile: boolean;
}) {
  const modalInstance = DialogPlugin.confirm({
    closeOnEscKeydown: false,
    closeOnOverlayClick: false,
    preventScrollThrough: true,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    cancelBtn: false,
    closeBtn: false,
    confirmBtn: 'OK',
    header: <h5>Application Already Submitted</h5>,
    body: <p>You have already submitted an application—no need to resubmit.</p>,
    onClose() {
      modalInstance.hide();
    },
    onConfirm() {
      modalInstance.hide();
    },
    ...options,
  });
  return modalInstance;
}

// 登录校验提示 - 英文
export function ShowCustomLoginTipsModal({
  content = '',
  isMobile = false,
  options = {},
}: {
  options?: DialogOptions;
  isMobile: boolean;
  content: string;
}) {
  const modalInstance = DialogPlugin.confirm({
    header: <h5>Please Log In</h5>,
    body: <p>{content}</p>,
    cancelBtn: 'Cancel',
    confirmBtn: 'Log In Now',
    closeOnEscKeydown: true,
    closeOnOverlayClick: true,
    preventScrollThrough: true,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    onClose: () => {
      modalInstance.hide();
    },
    ...options,
  });
  return modalInstance;
}

// 登录校验提示 - 中文
export function ShowCustomLoginTipsZhModal({
  content = '',
  isMobile = false,
  options = {},
}: {
  options?: DialogOptions;
  isMobile: boolean;
  content: string;
}) {
  const modalInstance = DialogPlugin.confirm({
    header: <h5>请登录</h5>,
    body: <p>{content}</p>,
    cancelBtn: '取消',
    confirmBtn: '登录',
    closeOnEscKeydown: true,
    closeOnOverlayClick: true,
    preventScrollThrough: true,
    showOverlay: true,
    mode: 'modal',
    placement: 'center',
    width: isMobile ? '88%' : 500,
    onClose: () => {
      modalInstance.hide();
    },
    ...options,
  });
  return modalInstance;
}
